import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { RUNNING_BUILD_ID } from './build-version';
import { CriticalOperationService } from './critical-operation.service';

export interface ReleaseMetadata { buildId: string; publishedAt: string; }
const POSTPONED_KEY = 'cc.postponedBuildId';
const CHECK_INTERVAL = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AppVersionService implements OnDestroy {
  readonly runningBuildId = RUNNING_BUILD_ID;
  deployedBuildId: string | null = null;
  private readonly available = new BehaviorSubject(false);
  readonly updateAvailable$ = this.available.asObservable();
  private readonly reloadMessage = new BehaviorSubject('');
  readonly reloadMessage$ = this.reloadMessage.asObservable();
  private postponedBuildId: string | null = this.readPostponed();
  private started = false;
  private destroyed = false;
  private interval?: ReturnType<typeof setInterval>;
  private request?: Promise<void>;
  private controller?: AbortController;
  private lastCheckAt = 0;

  constructor(private critical: CriticalOperationService) {}

  start(): void {
    if (this.started || this.destroyed || this.runningBuildId === 'development') return;
    this.started = true;
    void this.checkForUpdate();
    window.addEventListener('focus', this.onFocus);
    document.addEventListener('visibilitychange', this.onVisible);
    this.interval = setInterval(() => {
      if (document.visibilityState === 'visible') void this.checkForUpdate();
    }, CHECK_INTERVAL);
  }

  private readonly onFocus = () => {
    if (document.visibilityState === 'visible' && Date.now() - this.lastCheckAt > 1000) void this.checkForUpdate();
  };
  private readonly onVisible = () => {
    if (document.visibilityState === 'visible') this.onFocus();
  };

  checkForUpdate(): Promise<void> {
    if (this.destroyed) return Promise.resolve();
    if (this.request) return this.request;
    this.lastCheckAt = Date.now();
    this.controller = new AbortController();
    const timeout = setTimeout(() => this.controller?.abort(), 10000);
    // Native fetch deliberately bypasses authenticated API interceptors.
    this.request = this.fetchRelease(this.controller.signal).finally(() => {
      clearTimeout(timeout);
      this.request = undefined;
      this.controller = undefined;
    });
    return this.request;
  }

  private async fetchRelease(signal: AbortSignal): Promise<void> {
    try {
      const response = await fetch('/version.json', { cache: 'no-store', credentials: 'omit', signal });
      if (!response.ok) return;
      const release: ReleaseMetadata = await response.json();
      if (this.destroyed || !release || typeof release.buildId !== 'string'
        || !/^[A-Za-z0-9._-]{1,128}$/.test(release.buildId)
        || typeof release.publishedAt !== 'string' || !Number.isFinite(Date.parse(release.publishedAt))) return;
      const changed = this.deployedBuildId !== release.buildId;
      this.deployedBuildId = release.buildId;
      if (changed) this.reloadMessage.next('');
      if (release.buildId === this.runningBuildId) {
        this.available.next(false);
        this.reloadMessage.next('');
        this.postponedBuildId = null;
        this.writePostponed(null);
      } else {
        this.available.next(release.buildId !== this.postponedBuildId);
      }
    } catch {
      // Keep the last verified state; retry only at the next normal check.
    }
  }

  later(): void {
    if (!this.deployedBuildId || this.deployedBuildId === this.runningBuildId) return;
    this.postponedBuildId = this.deployedBuildId;
    this.writePostponed(this.postponedBuildId);
    this.available.next(false);
    this.reloadMessage.next('');
  }

  requestReload(): boolean {
    if (!this.available.value || !this.deployedBuildId) return false;
    if (this.critical.blocked) {
      this.reloadMessage.next('An update is ready. Finish your current action before reloading.');
      return false;
    }
    // No acknowledgement is saved. The next boot compares the actual bundle again.
    window.location.reload();
    return true;
  }

  private readPostponed(): string | null {
    try { return sessionStorage.getItem(POSTPONED_KEY); } catch { return null; }
  }
  private writePostponed(value: string | null): void {
    try {
      if (value) sessionStorage.setItem(POSTPONED_KEY, value);
      else sessionStorage.removeItem(POSTPONED_KEY);
    } catch { /* In-memory postponement still works when storage is unavailable. */ }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.interval) clearInterval(this.interval);
    window.removeEventListener('focus', this.onFocus);
    document.removeEventListener('visibilitychange', this.onVisible);
    this.controller?.abort();
    this.available.complete();
    this.reloadMessage.complete();
  }
}
